/**
 * Guards the login route ('' in app.module.ts). Structurally prevents a second
 * user from ever reaching the login form while this browser profile already has
 * a live server-confirmed session — closing the multi-user-login gap at its root
 * rather than patching it after the form is shown. Replaces the old, unwired
 * AuthGuard, which only ever checked `!!localStorage.getItem('userData')` and was
 * never actually attached to any route.
 */
import { Injectable, Injector } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { AuthService } from './auth.service';
import { AppEnvService } from './services/app-env.service';

@Injectable({
  providedIn: 'root'
})
export class LoginGuard implements CanActivate {
  // AuthService is deliberately NOT constructor-injected here. Constructor
  // injection resolves (and therefore constructs) the whole AuthService ->
  // UserService -> EnvironmentService chain the moment Angular builds this
  // guard - which the Router can do before provideAppInitializer's env.load()
  // has resolved (timing that differs enough between tabs to reproduce reliably
  // in a second tab). By the time canActivate() runs, that construction has
  // already happened or already crashed; awaiting inside canActivate() is too
  // late to prevent it. Injector.get() below defers the actual construction of
  // that chain until after load() is confirmed done.
  constructor(private router: Router, private appEnv: AppEnvService, private injector: Injector) {}

  async canActivate(): Promise<boolean | UrlTree> {
    await this.appEnv.load();
    const authService = this.injector.get(AuthService);
    const isAuthenticated = await authService.refresh();
    return isAuthenticated ? this.router.parseUrl('/node') : true;
  }
}
